import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { apiErr } from "../../lib/api";
import { useLang } from "../../i18n";
import { PhaseIntro } from "../../components/phase-common";
import { Button } from "../../components/ui/button";
import { Lock, Wand2, BookOpen, Crown, Store, FileDown } from "lucide-react";

const SECTIONS_NL = [
  ["ordinary_world", "De gewone wereld"],
  ["the_villain", "De vijand (het probleem van de klant)"],
  ["call_to_adventure", "De roep tot avontuur"],
  ["the_guide", "De gids (nederige marktkoopman)"],
  ["the_journey", "De reis"],
  ["transformation", "De transformatie"],
  ["glory", "De glorie"],
];
const SECTIONS_EN = [
  ["ordinary_world", "The ordinary world"],
  ["the_villain", "The villain (the customer's problem)"],
  ["call_to_adventure", "The call to adventure"],
  ["the_guide", "The guide (humble market vendor)"],
  ["the_journey", "The journey"],
  ["transformation", "The transformation"],
  ["glory", "The glory"],
];

export default function Phase6({ org, refresh }) {
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const locked = !org.core_emotion;
  const story = org.hero_journey || {};
  const sections = lang === "nl" ? SECTIONS_NL : SECTIONS_EN;
  const hasStory = story.manifesto || story.glory;

  const generate = async () => {
    setBusy(true);
    try { await api.post("/ai/hero-journey", { lang }); refresh(); toast.success("Glory Story"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 56;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2 - 30; // room for footer

      const pages = [];
      if (story.manifesto) pages.push({ title: org.core_emotion || t("manifesto"), body: story.manifesto });
      sections.forEach(([k, label]) => { if (story[k]) pages.push({ title: label, body: story[k] }); });

      const fit = (title, body) => {
        for (let fs = 96; fs >= 11; fs--) {
          const titleFs = Math.round(fs * 1.35);
          doc.setFont("times", "bold"); doc.setFontSize(titleFs);
          const tLines = doc.splitTextToSize(title, availW);
          doc.setFont("times", "normal"); doc.setFontSize(fs);
          const bLines = doc.splitTextToSize(body, availW);
          const h = tLines.length * titleFs * 1.16 + 26 + bLines.length * fs * 1.4;
          if (h <= availH) return { fs, titleFs, tLines, bLines, h };
        }
        doc.setFont("times", "bold"); doc.setFontSize(15);
        const tLines = doc.splitTextToSize(title, availW);
        doc.setFont("times", "normal"); doc.setFontSize(11);
        const bLines = doc.splitTextToSize(body, availW);
        const h = tLines.length * 15 * 1.16 + 26 + bLines.length * 11 * 1.4;
        return { fs: 11, titleFs: 15, tLines, bLines, h };
      };

      pages.forEach((p, idx) => {
        if (idx > 0) doc.addPage();
        const { fs, titleFs, tLines, bLines, h } = fit(p.title, p.body);
        const nLines = tLines.length + bLines.length;
        let extra = (availH - h) / Math.max(1, nLines);
        extra = Math.max(0, Math.min(extra, fs * 0.9));
        const usedH = h + extra * nLines;
        let y = margin + Math.max(0, (availH - usedH) / 2);

        doc.setFont("times", "bold"); doc.setFontSize(titleFs); doc.setTextColor(15, 76, 129);
        tLines.forEach((l) => { y += titleFs * 1.16 + extra; doc.text(l, pageW / 2, y, { align: "center" }); });

        y += 26;
        doc.setFont("times", "normal"); doc.setFontSize(fs); doc.setTextColor(26, 37, 64);
        bLines.forEach((l) => { y += fs * 1.4 + extra; doc.text(l, pageW / 2, y, { align: "center" }); });

        doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(150, 160, 175);
        const footer = `core-emotion.com  ·  ${org.core_emotion || ""}  ·  ${idx + 1}/${pages.length}`;
        doc.text(footer, pageW / 2, pageH - 28, { align: "center" });
      });

      const slug = String(org.core_emotion || "story").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      doc.save(`legend-${slug}.pdf`);
    } catch (e) {
      toast.error(lang === "nl" ? "PDF maken mislukt" : "PDF export failed");
    }
    setPdfBusy(false);
  };

  return (
    <div className="space-y-6">
      <PhaseIntro phase={6} title={t("p6_title")} intro={t("p6_intro")} />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex items-center gap-3 rounded-2xl border border-primary/20 bg-secondary p-5">
          <Crown className="h-7 w-7 text-accent" />
          <div><p className="font-display font-semibold text-primary">{lang === "nl" ? "De klant = held" : "The customer = hero"}</p></div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-primary/20 bg-secondary p-5">
          <Store className="h-7 w-7 text-accent" />
          <div><p className="font-display font-semibold text-primary">{lang === "nl" ? "De organisatie = nederige marktkoopman" : "The organization = humble market vendor"}</p></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="generate-story-btn" onClick={generate} disabled={locked || busy} className="rounded-full">
          <Wand2 className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {t("generate_story")}
        </Button>
        <Button data-testid="download-legend-pdf" onClick={downloadPdf} disabled={locked || pdfBusy || !hasStory} variant="outline"
          className="rounded-full border-primary/40 text-primary">
          <FileDown className={`mr-2 h-4 w-4 ${pdfBusy ? "animate-pulse" : ""}`} /> {t("download_pdf")}
        </Button>
      </div>

      {locked && (
        <div data-testid="phase6-locked" className="flex items-start gap-3 rounded-2xl border-2 border-dashed border-border bg-card p-5">
          <Lock className="mt-0.5 h-6 w-6 shrink-0 text-muted-foreground" />
          <p className="text-base leading-relaxed text-muted-foreground">{t("locked_phase6")}</p>
        </div>
      )}

      {!locked && hasStory && (
        <div className="space-y-4">
          {story.manifesto && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-card p-8 text-center">
              <BookOpen className="mx-auto h-7 w-7 text-accent" />
              <p className="mt-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">{t("manifesto")}</p>
              {org.core_emotion && (
                <p className="mt-1 font-display text-3xl font-bold text-primary">{org.core_emotion}</p>
              )}
              <p data-testid="manifesto-text" className="mt-3 font-display text-2xl font-semibold italic leading-snug text-primary">{story.manifesto}</p>
            </motion.div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {sections.map(([k, label], i) => story[k] && (
              <motion.div key={k}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-6">
                <div className="mb-2 flex items-center gap-2">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                  <h4 className="font-display text-base font-semibold text-primary">{label}</h4>
                </div>
                <p className="text-sm leading-relaxed text-foreground">{story[k]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
